import React, { useState, useEffect, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import Landing from "@/pages/Landing";
import LandingAr from "@/pages/LandingAr";

// ─────────────────────────────────────────────────────────────────
// Prototype E — Contextual Image-Reveal Header
//
// At rest: the header looks exactly like the production header.
// Text nav, black background, wordmark, language switcher.
//
// On hover/focus: the associated destination photograph crossfades
// into the central navigation zone as a full-bleed background
// behind the labels. Moving between destinations crossfades between
// their photographs. Leaving the nav returns the header to black.
//
// The photograph is revealed through the header — never attached to
// it. No cards, no thumbnails, no ribbons at any state.
//
// Preservation:
//   • /, /ar, /prototypes/arrival, /prototypes/ambient,
//     /prototypes/visual-header, /prototypes/cinematic-header,
//     /prototypes/integrated-ribbon — untouched.
// ─────────────────────────────────────────────────────────────────

// Destinations — using the existing labels of the production
// header (per brief). Only image URLs and object-position added.
const NAV_ITEMS = [
    {
        id: "home",
        to: "/",
        end: true,
        labelEn: "HOME",
        labelAr: "الرئيسية",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/5n6pde8o_FB21FACC-7058-44F0-AC7A-FA1B1432698C.JPG",
        objectPos: "50% 42%",
    },
    {
        id: "about",
        to: "/about",
        labelEn: "ABOUT",
        labelAr: "عن المؤسسة",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/u65gd357_IMG-20260503-WA0051.jpg",
        objectPos: "50% 40%",
    },
    {
        id: "gathering",
        to: "/gathering",
        labelEn: "THE GATHERING",
        labelAr: "The Gathering",
        protectedEn: true,
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/tf8y44s5_F3209F41-0B30-4C13-8807-7FCBDFB18ABD.PNG",
        objectPos: "50% 42%",
    },
    {
        id: "events",
        to: "/events",
        labelEn: "EVENTS",
        labelAr: "الفعاليات",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/l3h9h8v0_35B1CA2D-6A44-433F-A0BB-19EE971F256E.JPEG",
        objectPos: "50% 48%",
    },
    {
        id: "gallery",
        to: "/gallery",
        labelEn: "GALLERY",
        labelAr: "المعرض",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/eyed6v8w_E4BE464A-17CA-400C-9126-D68CE352C4E2.JPG",
        objectPos: "50% 50%",
    },
    {
        id: "sponsors",
        to: "/sponsors",
        labelEn: "SPONSORS",
        labelAr: "الرعاة",
        image: null, // No authentic partner activation available
    },
    {
        id: "store",
        to: "/store",
        labelEn: "STORE",
        labelAr: "المتجر",
        image: null, // No authentic CrewZNatioN merchandise photograph
    },
    {
        id: "contact",
        to: "/contact",
        labelEn: "CONTACT",
        labelAr: "تواصل",
        image: null, // No authentic hospitality/invitation photograph
    },
];

// Renders "The Gathering" with gold accent on "Gathering", matching
// the existing production Navbar treatment.
function renderLabel(item, isAr) {
    if (item.labelEn === "THE GATHERING" || item.labelEn === "The Gathering") {
        return (
            <span dir="ltr" className="inline-block">
                THE <span className="text-[var(--crewz-gold)]">GATHERING</span>
            </span>
        );
    }
    if (item.protectedEn && isAr) {
        return (
            <span dir="ltr" className="inline-block">
                {item.labelEn}
            </span>
        );
    }
    return isAr ? item.labelAr : item.labelEn;
}

function RevealNav({ isAr, toggleTo }) {
    const [activeId, setActiveId] = useState(null);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
        setActiveId(null);
    }, [location.pathname]);

    const clear = useCallback(() => setActiveId(null), []);

    return (
        <header
            className="reveal-header sticky top-0 z-50 bg-[#111111]/95 backdrop-blur-xl border-b border-white/5"
            data-testid="site-navbar-reveal"
            dir={isAr ? "rtl" : "ltr"}
        >
            <div className="px-6 md:px-10 lg:px-16 h-24 flex items-stretch justify-between">
                <nav
                    className={`reveal-nav-zone hidden lg:flex flex-1 relative order-2 ${
                        isAr ? "lg:ml-8 xl:ml-12" : "lg:mr-8 xl:mr-12"
                    }`}
                    aria-label="Primary navigation"
                    onMouseLeave={clear}
                    data-testid="reveal-nav-zone"
                >
                    {/* Image-reveal layer — behind labels. All images
                        pre-mounted, opacity 0 at rest. Only the
                        activeId's image crossfades to opacity 1. Ends
                        fade to black through the wordmark / utility
                        zones so nothing appears "pasted on". */}
                    <div className="reveal-image-layer absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
                        {NAV_ITEMS.filter((i) => i.image).map((item) => (
                            <img
                                key={item.id}
                                src={item.image}
                                alt=""
                                className={`reveal-image absolute inset-0 w-full h-full object-cover select-none ${
                                    activeId === item.id ? "is-active" : ""
                                }`}
                                style={{ objectPosition: item.objectPos }}
                                draggable={false}
                            />
                        ))}
                        {/* Shared dark grade + top/bottom vignette —
                            keeps labels legible while the photograph
                            plays as atmosphere behind them. */}
                        <div
                            className="reveal-vfade absolute inset-0"
                            style={{
                                background:
                                    "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 30%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.6) 100%)",
                            }}
                        />
                        {/* End fades — the image dissolves into the
                            surrounding wordmark and utility zones. */}
                        <div
                            className="reveal-endfade-start absolute inset-y-0 left-0 pointer-events-none"
                            style={{
                                width: "96px",
                                background:
                                    "linear-gradient(to right, #111111 0%, rgba(17,17,17,0.55) 55%, transparent 100%)",
                            }}
                        />
                        <div
                            className="reveal-endfade-end absolute inset-y-0 right-0 pointer-events-none"
                            style={{
                                width: "96px",
                                background:
                                    "linear-gradient(to left, #111111 0%, rgba(17,17,17,0.55) 55%, transparent 100%)",
                            }}
                        />
                    </div>

                    {/* Labels layer — production-familiar text nav */}
                    <div className="relative z-10 flex-1 flex items-center justify-between gap-1 font-body text-[13px] tracking-[0.2em] uppercase font-bold">
                        {NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.id}
                                to={item.to}
                                end={item.end}
                                onMouseEnter={() => setActiveId(item.id)}
                                onFocus={() => setActiveId(item.id)}
                                onBlur={clear}
                                className={({ isActive }) => {
                                    const isHovered = activeId === item.id;
                                    const anyActive = activeId !== null;
                                    if (isActive) return "reveal-label px-3 py-2 text-center transition-colors duration-300 text-[var(--crewz-gold)]";
                                    if (isHovered) return "reveal-label px-3 py-2 text-center transition-colors duration-300 text-[var(--crewz-gold)]";
                                    if (anyActive) return "reveal-label px-3 py-2 text-center transition-colors duration-300 text-zinc-400";
                                    return "reveal-label px-3 py-2 text-center transition-colors duration-300 text-zinc-200 hover:text-white";
                                }}
                                data-testid={`reveal-nav-${item.id}`}
                            >
                                {renderLabel(item, isAr)}
                            </NavLink>
                        ))}

                        <Link
                            to={toggleTo}
                            onMouseEnter={clear}
                            onFocus={clear}
                            className={
                                isAr
                                    ? "font-body uppercase tracking-[0.2em] px-3 py-2 text-[13px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                                    : "font-arabic normal-case tracking-normal px-3 py-2 text-[15px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                            }
                            dir={isAr ? "ltr" : "rtl"}
                            data-testid="reveal-lang-switcher"
                        >
                            {isAr ? "English" : "العربية"}
                        </Link>
                    </div>
                </nav>

                <Link
                    to={isAr ? "/prototypes/image-reveal-header?lang=ar" : "/prototypes/image-reveal-header"}
                    className="flex items-center gap-3 order-1 lg:order-4 self-center"
                    data-testid="reveal-nav-logo"
                    onMouseEnter={clear}
                >
                    <img
                        src={LOGO_URL}
                        alt="CrewZNatioN"
                        className="h-14 md:h-16 w-auto select-none"
                        draggable={false}
                    />
                </Link>

                <button
                    className="lg:hidden text-white p-2 order-2 self-center"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="reveal-mobile-toggle"
                    aria-label={isAr ? "فتح القائمة" : "Toggle menu"}
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/5 bg-[#111111]/95 backdrop-blur-xl">
                    <div className={`px-6 py-6 flex flex-col gap-1 ${isAr ? "font-arabic" : "font-body"}`}>
                        {NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.id}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `px-2 py-3 text-xs tracking-[0.22em] uppercase border-b border-white/5 ${
                                        isActive ? "text-[var(--crewz-gold)]" : "text-zinc-300"
                                    }`
                                }
                            >
                                {renderLabel(item, isAr)}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}

export default function ImageRevealPrototype() {
    const location = useLocation();
    const isAr = location.search.includes("lang=ar");
    const toggleTo = isAr
        ? "/prototypes/image-reveal-header"
        : "/prototypes/image-reveal-header?lang=ar";

    return (
        <div
            className="image-reveal-prototype min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]"
            data-testid="image-reveal-prototype"
            dir={isAr ? "rtl" : "ltr"}
        >
            <RevealNav isAr={isAr} toggleTo={toggleTo} />

            <div className="image-reveal-body">
                {isAr ? <LandingAr /> : <Landing />}
            </div>

            <style>{`
                .image-reveal-prototype .image-reveal-body [data-testid="site-navbar"],
                .image-reveal-prototype .image-reveal-body [data-testid="site-navbar-ar"] {
                    display: none !important;
                }

                /* Image reveal — opacity crossfade only, no scale, no
                   transform theatrics. Restrained cinematic grade at
                   the moment of reveal. */
                .image-reveal-prototype .reveal-image {
                    opacity: 0;
                    filter: brightness(0.78) saturate(0.85) contrast(1.05);
                    transition:
                        opacity 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
                    will-change: opacity;
                }
                .image-reveal-prototype .reveal-image.is-active {
                    opacity: 1;
                }

                /* Vertical vignette also fades in with the image so
                   the rest state stays true black. */
                .image-reveal-prototype .reveal-vfade {
                    opacity: 0;
                    transition: opacity 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
                    pointer-events: none;
                }
                .image-reveal-prototype .reveal-nav-zone:hover .reveal-vfade,
                .image-reveal-prototype .reveal-nav-zone:focus-within .reveal-vfade {
                    opacity: 1;
                }

                /* End fades — always present so the reveal never
                   reads as a pasted-on strip. */
                .image-reveal-prototype .reveal-endfade-start,
                .image-reveal-prototype .reveal-endfade-end {
                    opacity: 0;
                    transition: opacity 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
                }
                .image-reveal-prototype .reveal-nav-zone:hover .reveal-endfade-start,
                .image-reveal-prototype .reveal-nav-zone:focus-within .reveal-endfade-start,
                .image-reveal-prototype .reveal-nav-zone:hover .reveal-endfade-end,
                .image-reveal-prototype .reveal-nav-zone:focus-within .reveal-endfade-end {
                    opacity: 1;
                }

                /* Label text shadow only during reveal — soft
                   legibility guard without changing rest typography. */
                .image-reveal-prototype .reveal-nav-zone:hover .reveal-label,
                .image-reveal-prototype .reveal-nav-zone:focus-within .reveal-label {
                    text-shadow: 0 1px 2px rgba(0,0,0,0.6);
                }

                @media (prefers-reduced-motion: reduce) {
                    .image-reveal-prototype .reveal-image,
                    .image-reveal-prototype .reveal-vfade,
                    .image-reveal-prototype .reveal-endfade-start,
                    .image-reveal-prototype .reveal-endfade-end {
                        transition: none;
                    }
                }
            `}</style>
        </div>
    );
}
